import React, { useState, useEffect } from 'react';
import TopNavbar from '../../components/TopNavbar';
import SideNavbar from '../../components/SideNavbar';
import { collection, query, where, getDocs, doc, getDoc, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useStudentAuth } from '../../StudentAuthContext';
import Toast from '../../components/Toast';
import { useAppSelector } from '../../redux/store';

interface Escalation {
    id: string;
    requestedById: string;
    assigneeId: string;
    createdDate: Timestamp;
    isActive: boolean;
    isDone: boolean;
    description: string;
}

const StudentEscalations = () => {
    const [description, setDescription] = useState('');
    const [wordCount, setWordCount] = useState(0);
    const [escalations, setEscalations] = useState<Escalation[]>([]);
    const [isCreating, setIsCreating] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

    const { currentStudent } = useStudentAuth();

    useEffect(() => {
        if (currentStudent?.companyId && currentStudent?.leadUid) {
            fetchEscalations();
        }
    }, [currentStudent]);

    const fetchEscalations = async () => {
        if (!currentStudent?.companyId || !currentStudent?.leadUid) return;

        try {
            const escalationsRef = collection(db, `companies/${currentStudent.companyId}/escalations`);
            const q = query(
                escalationsRef,
                where('requestedById', '==', currentStudent.leadUid),
                where('isActive', '==', true)
            );

            const querySnapshot = await getDocs(q);
            const escalationsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Escalation[];

            setEscalations(escalationsData);
        } catch (error) {
            console.error('Error fetching escalations:', error);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value;
        setDescription(text);
        const words = text.trim() ? text.trim().split(/\s+/).length : 0;
        setWordCount(words);
    };

    const handleRaiseEscalation = async () => {
        if (!description.trim() || !currentStudent?.companyId || !currentStudent?.leadUid) return;

        try {
            setIsCreating(true);
            // Get the assignedTo field from the lead document
            const leadDoc = await getDoc(doc(db, `companies/${currentStudent.companyId}/leads/${currentStudent.leadUid}`));
            const leadData = leadDoc.data();
            if (!leadData?.assignedTo) {
                console.error('No assigned staff found');
                return;
            }

            const escalationData = {
                requestedById: currentStudent.leadUid,
                assigneeId: leadData.assignedTo,
                createdDate: Timestamp.now(),
                isActive: true,
                isDone: false,
                description: description.trim()
            };

            const escalationsRef = collection(db, `companies/${currentStudent.companyId}/escalations`);
            await addDoc(escalationsRef, escalationData);

            // Reset form and refresh escalations
            setDescription('');
            setWordCount(0);
            fetchEscalations();
            setToastMessage('Escalation created successfully!');
            setIsErrorToast(false);
            setShowToast(true);
        } catch (error) {
            setToastMessage('Error creating escalation.Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
            console.error('Error creating escalation:', error);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>
            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300`}>
                <div className="bg-white min-h-screen p-6 rounded-3xl shadow-md w-full">
                    <div className="flex items-center gap-6 mb-6">
                        <div
                            className="flex items-center justify-center w-14 h-14 rounded-full"
                            style={{ background: 'linear-gradient(180deg, #00A6D9 10%, #3483BE 100%)' }}>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/desktop/solar_flag-broken.svg`}
                                alt="Ticket Icon" className="w-8 h-8" />
                        </div>
                        <h2 className="font-montserrat text-2xl font-medium text-gray-900">
                            Escalations
                        </h2>
                    </div>

                    <div className="px-8">
                        <p className="font-montserrat text-xl font-meduim bg-gradient-to-b from-[#3483BE] to-[#02385E] text-transparent bg-clip-text">
                            Escalate any unresolved issue with the management with a single click
                        </p>
                    </div>

                    <div className="mt-8 px-0">
                        <div className="w-4/5 flex">
                            <div className="w-1/5">
                                <div className="rounded-lg flex items-center justify-center">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/flat-color-icons_customer-support.svg`}
                                        alt="Ticket Icon" className="w-20 h-20" />
                                </div>
                            </div>
                            <div className="w-4/5 relative">
                                <textarea
                                    className="w-full p-4 h-52 text-sm text-gray-700 border border-cyan-600 rounded-xl focus:ring-2 focus:ring-sky-500 resize-none"
                                    placeholder="Briefly describe the issue that you are facing"
                                    onChange={handleInputChange}
                                    value={description}
                                    maxLength={500} />
                                <div className="absolute bottom-20 right-2 text-xs font-montserrat text-zinc-500">
                                    {wordCount}/500 words
                                </div>
                                <button
                                    onClick={handleRaiseEscalation}
                                    disabled={!description.trim() || isCreating}
                                    className={`mt-6 text-white py-2 px-4 rounded-full flex items-center justify-center ${!description.trim() || isCreating ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                    style={{ background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)' }}>
                                    {isCreating ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Creating...
                                        </>
                                    ) : (
                                        'Raise Escalation'
                                    )}
                                </button>
                            </div>
                        </div>

                        <hr className="mt-10 mb-8 w-full"
                            style={{
                                border: "none",
                                height: "1px",
                                background: "linear-gradient(to right, #FFFFFF 0%, #72AAD2 33%, #5E9DCB 66%, #FFFFFF 100%)"
                            }}
                        />
                        <div className="rounded-3xl overflow-hidden shadow-lg">
                            <div className="overflow-x-auto relative">
                                <table className="min-w-full table-auto bg-zinc-100">
                                    <thead>
                                        <tr className="text-center bg-[#02385E] text-white">
                                            <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat w-[120px]">Ticket ID</th>
                                            <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat w-[140px]">Created On</th>
                                            <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat">Description</th>
                                            <th className="font-base font-normal p-2 text-zinc-100 font-Montserrat w-[120px]">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {escalations.map((escalation, index) => (
                                            <tr key={escalation.id} className={index % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}>
                                                <td className="p-2 text-center">{escalation.id.slice(0, 8)}</td>
                                                <td className="p-2 text-center">
                                                    {escalation.createdDate.toDate().toLocaleDateString()}
                                                </td>
                                                <td className="p-2 text-left">{escalation.description}</td>
                                                <td className="p-2 text-center">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${escalation.isDone
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-yellow-100 text-yellow-800'
                                                        }`}>
                                                        {escalation.isDone ? 'Done' : 'Pending'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default StudentEscalations;